import { useState, useEffect, useCallback } from 'react'

const API_URL = process.env.REACT_APP_API_URL
const STORAGE_KEY = 'crypto_portfolio_data'
const TIMESTAMP_KEY = 'crypto_portfolio_timestamp'
const MAX_AGE = 1000 * 60 * 60 // 1 година

export const usePortfolioData = () => {
	const [portfolioData, setPortfolioData] = useState([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)
	const [nearestData, setNearestData] = useState(null)
	const [yesterdayData, setYesterdayData] = useState(null)
	const [twoDaysAgoData, setTwoDaysAgoData] = useState(null)
	const [windowWidth, setWindowWidth] = useState(window.innerWidth)

	const fetchAndStoreData = async () => {
		try {
			const response = await fetch(API_URL)
			if (!response.ok) throw new Error('Failed to fetch')
			const data = await response.json()

			localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
			localStorage.setItem(TIMESTAMP_KEY, Date.now().toString())
			setPortfolioData(data)
		} catch (err) {
			setError('Failed to fetch data.')
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		const storedData = localStorage.getItem(STORAGE_KEY)
		const timestamp = parseInt(localStorage.getItem(TIMESTAMP_KEY), 10)

		const isFresh = timestamp && Date.now() - timestamp < MAX_AGE

		if (storedData && isFresh) {
			setPortfolioData(JSON.parse(storedData))
			setLoading(false)
		} else {
			fetchAndStoreData()
		}

		const handleResize = () => setWindowWidth(window.innerWidth)
		window.addEventListener('resize', handleResize)
		return () => window.removeEventListener('resize', handleResize)
	}, [])

	const findDataByDate = useCallback(
		daysAgo => {
			const targetDate = new Date()
			targetDate.setDate(targetDate.getDate() - daysAgo)
			const start = new Date(targetDate.setHours(0, 0, 0, 0))
			const end = new Date(targetDate.setHours(23, 59, 59, 999))

			let closest = null
			let minDiff = Infinity

			portfolioData.forEach(item => {
				const itemTime = new Date(item.time)
				if (itemTime >= start && itemTime <= end) {
					const diff = Math.abs(itemTime - start)
					if (diff < minDiff) {
						minDiff = diff
						closest = item
					}
				}
			})
			return closest
		},
		[portfolioData]
	)

	const findNearest = useCallback(() => {
		const now = new Date()
		let nearest = null
		let minDiff = Infinity

		portfolioData.forEach(item => {
			const time = new Date(item.time)
			const diff = Math.abs(time - now)
			if (diff < minDiff) {
				minDiff = diff
				nearest = item
			}
		})
		return nearest
	}, [portfolioData])

	useEffect(() => {
		if (portfolioData.length > 0) {
			setNearestData(findNearest())
			setYesterdayData(findDataByDate(1))
			setTwoDaysAgoData(findDataByDate(2))
		}
	}, [portfolioData, findNearest, findDataByDate])

	return {
		portfolioData,
		loading,
		error,
		nearestData,
		yesterdayData,
		twoDaysAgoData,
		windowWidth,
	}
}
